import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useLoginMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks/redux';
import { setTempAuthToken, setToken } from '@/store/slices/authSlice';
import toast from 'react-hot-toast';
import { LoginFormData, loginSchema } from '@/utils/validations';
import CustomInput from '@/components/common/CustomInput';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { Button } from '@/components/ui/button';

function LoginPage() {
  const [finalError, setFinalError] = useState<string | null>(null);
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { control, handleSubmit, reset } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setFinalError(null);
    try {
      const payload = {
        username: data.username,
        password: data.password,
      };
      const res = await login(payload).unwrap();

      if (res?.data?.access_token) {
        dispatch(
          setToken({
            accessToken: res.data.access_token,
            refreshToken: res.data.refresh_token,
            tokenType: res.data.token_type,
          })
        );
        reset();
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      console.error('Login failed:', err);
      const message = err?.data?.message || err?.message || 'Login failed!';
      setFinalError(message);
      toast.error(message);
    }
  };

  return (
    <div className="w-full font-poppins">
      {/* Header */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 mb-4">
          <div className="w-8 h-0.5 bg-primary rounded-full" />
          <span className="text-blue text-x\s font-semibold tracking-widest uppercase">
            Welcome back
          </span>
        </div>
        <h1 className="text-3xl font-bold text-blue font-rokkit mb-1">Sign in to your account</h1>
        <p className="text-gray-400 text-sm">Enter your credentials to access the panel</p>
      </div>

      {finalError && (
        <div className="mb-6">
          <ErrorBanner type="error" title="Error" message={finalError} />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <CustomInput
          name="username"
          control={control}
          label="Email Address"
          placeholder="name@example.com"
          type="text"
          inputClassName="border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 font-poppins"
          labelClassName="font-medium text-gray-600 text-sm"
        />

        <CustomInput
          name="password"
          control={control}
          label="Password"
          type="password"
          placeholder="Enter your password"
          inputClassName="border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 font-poppins"
          labelClassName="font-medium text-gray-600 text-sm"
        />

        <div className="pt-3">
          <Button
            type="submit"
            className="w-full h-11 font-poppins font-semibold uppercase tracking-wide rounded-lg"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
export default LoginPage;
